import { useState } from 'react';
import { Alert, App, Button, Card, InputNumber, Space, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { paiseToRupees, rupeesToPaise } from '../../lib/money';

/** Server shape of GET/PATCH /admin/chat-pricing. All integers — money is PAISE. */
export interface ChatPricingConfig {
  messagesPerBatch: number;
  coinsPerBatch: number;
  /** Completed conversation cycles (user message(s) → listener's first reply) per listener earning. */
  cyclesPerEarning: number;
  /** The listener's earning per CYCLE batch, in paise. */
  listenerEarnPaisePerBatch: number;
}

/** Allowed ranges — the server enforces the same bounds. */
export const CHAT_PRICING_LIMITS = {
  messagesPerBatch: { min: 1, max: 50 },
  coinsPerBatch: { min: 0, max: 100000 },
  // Entered in rupees (0–100000.00), stored in paise.
  listenerEarnPaisePerBatch: { min: 0, max: 10_000_000 },
  cyclesPerEarning: { min: 1, max: 100 },
} as const;

type Key = keyof ChatPricingConfig;

/** Only the fields that changed — an unchanged value is never re-written. */
export function changedChatPricing(
  saved: ChatPricingConfig,
  draft: Partial<ChatPricingConfig>,
): Partial<ChatPricingConfig> {
  const out: Partial<ChatPricingConfig> = {};
  for (const k of Object.keys(draft) as Key[]) {
    const v = draft[k];
    if (v !== undefined && v !== saved[k]) out[k] = v;
  }
  return out;
}

/**
 * Text chat pricing — two INDEPENDENT counters. The USER pays a coin charge
 * every N of their own messages. The LISTENER earns real money (₹) every N
 * completed conversation cycles (one or more user messages, then the
 * listener's first reply; more replies before the next user message count
 * once). Nothing here is shown inside the chat. The ₹ field is shown in rupees
 * but stored and sent as integer paise.
 */
export function TextChatPricingCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data } = useQuery({
    queryKey: ['admin', 'chat-pricing'],
    queryFn: async () => (await api.get<ChatPricingConfig>('/admin/chat-pricing')).data,
  });
  const [draft, setDraft] = useState<Partial<ChatPricingConfig>>({});

  const save = useMutation({
    mutationFn: async (body: Partial<ChatPricingConfig>) =>
      (await api.patch<ChatPricingConfig>('/admin/chat-pricing', body)).data,
    onSuccess: () => {
      message.success('Text chat pricing saved');
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['admin', 'chat-pricing'] });
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
          'Could not save',
      ),
  });

  if (!data) return null;
  const value = (k: Key) => draft[k] ?? data[k];
  const set = (k: Key) => (n: number | null) =>
    setDraft((d) => ({ ...d, [k]: n === null ? undefined : Math.round(n) }));
  const changes = changedChatPricing(data, draft);
  const L = CHAT_PRICING_LIMITS;

  return (
    <Card title="Text chat pricing">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Two separate counters. USER billing: the user pays the coin charge every N of THEIR OWN messages. LISTENER earning: real money (₹) every N completed conversation cycles — one or more user messages followed by the listener's first reply; several replies in a row count as one cycle. The listener is never charged, and none of this is shown in the chat."
        />

        <Space direction="vertical">
          <span>Messages per billing batch</span>
          <InputNumber
            data-testid="chat-messages-per-batch"
            min={L.messagesPerBatch.min}
            max={L.messagesPerBatch.max}
            precision={0}
            value={value('messagesPerBatch')}
            onChange={set('messagesPerBatch')}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            How many of the user&apos;s own messages make one billable batch.{' '}
            {L.messagesPerBatch.min}–{L.messagesPerBatch.max}, default 5.
          </Typography.Text>
        </Space>

        <Space direction="vertical">
          <span>User charge per batch (coins)</span>
          <InputNumber
            data-testid="chat-coins-per-batch"
            min={L.coinsPerBatch.min}
            max={L.coinsPerBatch.max}
            precision={0}
            suffix="coins"
            style={{ width: 160 }}
            value={value('coinsPerBatch')}
            onChange={set('coinsPerBatch')}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Coins debited from the user when a batch starts. {L.coinsPerBatch.min}–
            {L.coinsPerBatch.max}, default 25.
          </Typography.Text>
        </Space>

        <Space direction="vertical">
          <span>Completed cycles per listener earning</span>
          <InputNumber
            data-testid="chat-cycles-per-earning"
            min={L.cyclesPerEarning.min}
            max={L.cyclesPerEarning.max}
            precision={0}
            suffix="cycles"
            style={{ width: 160 }}
            value={value('cyclesPerEarning')}
            onChange={set('cyclesPerEarning')}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            One cycle = the user writes (one or more messages) and the listener replies. Replies in a
            row before the next user message are the same cycle. {L.cyclesPerEarning.min}–
            {L.cyclesPerEarning.max}, default 5.
          </Typography.Text>
        </Space>

        <Space direction="vertical">
          <span>Listener earning per cycle batch</span>
          <InputNumber
            data-testid="chat-listener-earn-rupees"
            min={paiseToRupees(L.listenerEarnPaisePerBatch.min)}
            max={paiseToRupees(L.listenerEarnPaisePerBatch.max)}
            step={0.5}
            precision={2}
            prefix="₹"
            style={{ width: 160 }}
            value={paiseToRupees(value('listenerEarnPaisePerBatch'))}
            onChange={(n) =>
              set('listenerEarnPaisePerBatch')(n === null ? null : rupeesToPaise(n))
            }
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Real money (₹) credited to the listener every N completed cycles, independent of the
            user&apos;s coin charge. ₹0–₹{paiseToRupees(L.listenerEarnPaisePerBatch.max).toLocaleString('en-IN')}, default ₹2.50.
          </Typography.Text>
        </Space>

        <Can permission="call_pricing.edit">
          <Button
            type="primary"
            disabled={Object.keys(changes).length === 0}
            loading={save.isPending}
            onClick={() => save.mutate(changes)}
          >
            Save
          </Button>
        </Can>
      </Space>
    </Card>
  );
}
